import { type ChangeEventHandler, type FC, useState } from 'react';
import { useUnit } from 'effector-react';
import { Flex, Group, Input, MultiSelect, NumberInput, Spoiler, TextInput } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import {
    type PayoutOrderPublicStatus,
    payoutOrderPublicStatuses,
    type PayoutOrderStatus,
    payoutOrderStatuses,
} from '~/entities/payout';
import { currencies, type Currency } from '~/entities/currency';
import { isUuid } from '~/shared/lib/utils/isUuid';
import { useBreakpoint } from '~/shared/lib/hooks/useBreakpoint';
import { useDebouncedField } from '~/shared/lib/hooks/useDebouncedField';
import { $filters, setFilters } from './model';
import { RequisiteFilter } from './components';

interface FiltersProps {
    onReset?: VoidFunction;
}

export const Filters: FC<FiltersProps> = ({ onReset }) => {
    const isMobile = useBreakpoint('mobile');

    const { filters } = useUnit({ filters: $filters });

    const [orderIdValue, setOrderIdValue] = useState(filters.orderId?.value ?? '');

    const { value: traderNameValue, onChange: handleChangeTraderName } = useDebouncedField({
        initialValue: filters.traderName?.value ?? '',
        onChange: (value: string) => setFilters({ traderName: { value } }),
    });

    const isOrderIdInvalid = orderIdValue !== '' && !isUuid(orderIdValue);

    const handleClearOrderId = () => {
        setOrderIdValue('');
        setFilters({
            orderId: {
                value: undefined,
            },
        });
        onReset?.();
    };

    const handleChangeOrderId: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
        setOrderIdValue(value);

        if (value === '') {
            handleClearOrderId();

            return;
        }

        if (!isUuid(value)) {
            return;
        }

        setFilters({
            orderId: {
                value,
            },
        });
        onReset?.();
    };

    const handleChangeStatuses = (value: string[]) => {
        setFilters({
            statuses: value as PayoutOrderStatus[],
        });
        onReset?.();
    };

    const handleChangePublicStatuses = (value: string[]) => {
        setFilters({
            publicStatuses: value as PayoutOrderPublicStatus[],
        });
        onReset?.();
    };

    const handleChangeCurrency = (value: string[]) => {
        setFilters({
            currency: value as Currency[],
        });
        onReset?.();
    };

    return (
        <>
            <Group
                align='flex-start'
                grow
                gap='md'
            >
                <Input.Wrapper
                    size='xs'
                    label='Поиск по ID ордера'
                    error={isOrderIdInvalid ? 'Введите корректный ID' : undefined}
                    flex={1}
                >
                    <Input
                        size='xs'
                        value={orderIdValue}
                        onChange={handleChangeOrderId}
                        rightSection={orderIdValue ? <Input.ClearButton onClick={handleClearOrderId} /> : undefined}
                        rightSectionPointerEvents='auto'
                    />
                </Input.Wrapper>

                <MultiSelect
                    clearable
                    placeholder={filters.statuses?.length === 0 ? 'Выберите статус' : ''}
                    value={filters.statuses}
                    data={payoutOrderStatuses}
                    label='Статус ордеров'
                    size='xs'
                    onChange={handleChangeStatuses}
                    flex={1}
                />
                <MultiSelect
                    clearable
                    placeholder={filters.publicStatuses?.length === 0 ? 'Выберите статус' : ''}
                    value={filters.publicStatuses}
                    data={payoutOrderPublicStatuses}
                    label='Публичный статус ордеров'
                    size='xs'
                    onChange={handleChangePublicStatuses}
                    flex={1}
                />

                <TextInput
                    label='Имя трейдера'
                    placeholder='Введите имя'
                    value={traderNameValue}
                    onChange={(e) => handleChangeTraderName(e.target.value)}
                />
            </Group>

            <Spoiler
                maxHeight={0}
                showLabel='Показать больше фильтров'
                hideLabel='Скрыть'
            >
                <Flex
                    mb='md'
                    direction='column'
                    gap='sm'
                >
                    <Flex
                        gap={!isMobile ? 8 : 4}
                        direction={!isMobile ? 'row' : 'column'}
                    >
                        <RequisiteFilter />
                        <DateTimePicker
                            clearable
                            size='xs'
                            value={filters.createdAtGt}
                            label='Создано от'
                            onChange={(value) => {
                                value
                                    ? setFilters({ createdAtGt: new Date(value).toISOString() })
                                    : setFilters({ createdAtGt: undefined });
                                onReset?.();
                            }}
                            flex={1}
                        />
                        <DateTimePicker
                            clearable
                            size='xs'
                            value={filters.createdAtLt}
                            label='Создано до'
                            onChange={(value) => {
                                value
                                    ? setFilters({ createdAtLt: new Date(value).toISOString() })
                                    : setFilters({ createdAtLt: undefined });
                                onReset?.();
                            }}
                            flex={1}
                        />
                        <MultiSelect
                            clearable
                            placeholder={filters.currency?.length === 0 ? 'Выберите валюту' : ''}
                            value={filters.currency}
                            data={currencies}
                            label='Валюта'
                            size='xs'
                            searchable
                            onChange={handleChangeCurrency}
                            flex={1}
                        />
                    </Flex>

                    <Flex
                        gap={!isMobile ? 8 : 4}
                        direction={!isMobile ? 'row' : 'column'}
                    >
                        <NumberInput
                            w='100%'
                            label='Сумма от'
                            onChange={(value) =>
                                value
                                    ? setFilters({ amountGt: { value: String(value) } })
                                    : setFilters({ amountGt: undefined })
                            }
                        />
                        <NumberInput
                            w='100%'
                            label='Сумма до'
                            onChange={(value) =>
                                value
                                    ? setFilters({ amountLt: { value: String(value) } })
                                    : setFilters({ amountLt: undefined })
                            }
                        />
                    </Flex>
                </Flex>
            </Spoiler>
        </>
    );
};
